import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ShareButtons from '../components/ShareButtons';
import homeNewsAPI from '../utils/api/homeNewsAPI';
import { hasTrackedView, markTrackedView, formatEngagementCount } from '../utils/contentEngagement';
import './NewsDetail.css';

const HOME_NEWS_FALLBACK_IMAGE = 'https://www.newsonair.gov.in/wp-content/uploads/2025/09/yogi.jpg';

function HomeNewsDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [moreNews, setMoreNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchNewsDetail = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await homeNewsAPI.getNewsBySlug(slug);

        if (data.success) {
          setNews(data.data);
        } else {
          setError('समाचार नहीं मिला');
        }
      } catch (fetchError) {
        console.error('Error fetching home news:', fetchError);
        setError('समाचार लोड करने में विफल रहे');
      } finally {
        setLoading(false);
      }
    };

    fetchNewsDetail();
  }, [slug]);

  useEffect(() => {
    const previousTitle = document.title;

    if (!news) {
      document.title = 'होम समाचार | लाठ साब';
      return () => {
        document.title = previousTitle;
      };
    }

    document.title = `${news.title} | लाठ साब`;

    return () => {
      document.title = previousTitle;
    };
  }, [news]);

  useEffect(() => {
    if (!news?._id || hasTrackedView('home-news', news._id)) {
      return;
    }

    const trackView = async () => {
      try {
        const response = await homeNewsAPI.incrementView(news._id);

        if (response.success && response.data?.views !== undefined) {
          setNews((current) => (current ? { ...current, views: response.data.views } : current));
        }

        markTrackedView('home-news', news._id);
      } catch (viewError) {
        console.error('Failed to increment home news view count:', viewError);
      }
    };

    trackView();
  }, [news?._id]);

  useEffect(() => {
    if (!news?._id) {
      return;
    }

    const fetchMoreNews = async () => {
      try {
        const [sameCategoryResponse, fallbackResponse] = await Promise.all([
          homeNewsAPI.getNews({ category: news.category, limit: 8 }),
          homeNewsAPI.getNews({ limit: 14 })
        ]);

        const sameCategoryItems = sameCategoryResponse.success ? sameCategoryResponse.data || [] : [];
        const fallbackItems = fallbackResponse.success ? fallbackResponse.data || [] : [];

        const merged = [...sameCategoryItems, ...fallbackItems].filter(
          (item, index, array) => item._id !== news._id && array.findIndex((entry) => entry._id === item._id) === index
        );

        setMoreNews(merged.slice(0, 6));
      } catch (fetchError) {
        console.error('Failed to fetch more home news:', fetchError);
        setMoreNews([]);
      }
    };

    fetchMoreNews();
  }, [news?._id, news?.category]);

  if (loading) {
    return (
      <div className="news-detail loading">
        <div className="spinner">लोड हो रहा है...</div>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="news-detail error">
        <div className="error-container">
          <h2>त्रुटि</h2>
          <p>{error || 'समाचार नहीं मिला'}</p>
          <button className="back-btn" onClick={() => navigate('/')}>
            होम पर जाएं
          </button>
        </div>
      </div>
    );
  }

  const newsUrl = `${window.location.origin}/home-news/${news.slug}`;
  const imageSrc = news.image || HOME_NEWS_FALLBACK_IMAGE;

  return (
    <article className="news-detail home-news-detail-page">
      <div className="news-detail-container home-news-detail-container">
        <div className="news-detail-main home-news-detail-main">
          <nav className="breadcrumb">
            <Link to="/">होम</Link>
            <span>/</span>
            <span>{news.category}</span>
            <span>/</span>
            <span>{news.title.substring(0, 50)}...</span>
          </nav>

          <header className="news-header home-news-header-card">
            <div className="home-news-kicker-row">
              <span className="category-badge">{news.category}</span>
              {news.location ? <span className="location-badge">{news.location}</span> : null}
            </div>
            <h1 className="news-title">{news.title}</h1>

            <div className="news-meta">
              <span className="date-badge">
                {new Date(news.createdAt).toLocaleDateString('hi-IN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              <span className="views-badge">👁️ {formatEngagementCount(news.views)} views</span>
              <span className="views-badge">🔗 {formatEngagementCount(news.shareCount)} shares</span>
            </div>

            <p className="news-short-description">{news.description}</p>
          </header>

          <figure className="news-feature-image home-news-feature-image">
            <img
              src={imageSrc}
              alt={news.title}
              loading="lazy"
              onError={(event) => {
                event.target.src = HOME_NEWS_FALLBACK_IMAGE;
              }}
            />
            <figcaption>समाचार छवि</figcaption>
          </figure>

          <div className="news-content home-news-content-card">
            {String(news.content || '')
              .split('\n')
              .filter(Boolean)
              .map((paragraph, index) => (
                <p key={`${news._id}-${index}`}>{paragraph}</p>
              ))}
          </div>

          <section className="home-news-share-panel">
            <div>
              <h3>इस खबर को साझा करें</h3>
              <p>अगर यह खबर जरूरी लगे तो इसे आगे भेजें।</p>
            </div>
            <ShareButtons
              title={news.title}
              url={newsUrl}
              description={news.description}
              contentId={news._id}
              contentType="home-news"
            />
          </section>
        </div>

        <aside className="news-detail-sidebar">
          <div className="sidebar-widget">
            <h4>महत्वपूर्ण संदर्भ</h4>
            <p className="info-item">श्रेणी: <strong>{news.category}</strong></p>
            <p className="info-item">लेखक: <strong>{news.author || 'Admin'}</strong></p>
            {news.location ? <p className="info-item">स्थान: <strong>{news.location}</strong></p> : null}
            <p className="info-item">रीड्स: <strong>{formatEngagementCount(news.views)}</strong></p>
            <p className="info-item">शेयर: <strong>{formatEngagementCount(news.shareCount)}</strong></p>
          </div>
        </aside>
      </div>

      {moreNews.length > 0 ? (
        <section className="home-news-more-section">
          <div className="home-news-more-header">
            <div>
              <span className="home-news-more-kicker">Continue Reading</span>
              <h2>और भी पढ़ें</h2>
            </div>
            <p>इसी तरह की और खबरें नीचे पढ़ें।</p>
          </div>

          <div className="home-news-more-grid">
            {moreNews.map((item) => {
              const itemImage = item.image || HOME_NEWS_FALLBACK_IMAGE;

              return (
                <Link key={item._id} to={`/home-news/${item.slug}`} className="home-news-more-card">
                  <img
                    src={itemImage}
                    alt={item.title}
                    onError={(event) => {
                      event.target.src = HOME_NEWS_FALLBACK_IMAGE;
                    }}
                  />
                  <div className="home-news-more-card-body">
                    <span className="home-news-more-chip">{item.category}</span>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <div className="home-news-more-meta">
                      <span>{item.location || 'लाठ साब'}</span>
                      <span>{new Date(item.createdAt).toLocaleDateString('hi-IN')}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      ) : null}
    </article>
  );
}

export default HomeNewsDetail;